import { Link } from 'react-router-dom';
import { Phone, ArrowRight, MapPin, Clock, Utensils } from 'lucide-react';
import { Button } from '../components/ui/button';
import { restaurantInfo } from '../data/menuData';

// Hero Section
const HeroSection = () => (
  <section className="hero-bg min-h-[90vh] flex items-center justify-center relative" data-testid="hero-section">
    <div className="max-w-7xl mx-auto px-4 md:px-8 text-center pt-20">
      {/* Logo Text */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-wide text-white uppercase mb-4">
          Délices <span className="text-orange-500">de</span> Chaumont
        </h1>
        <p className="font-accent text-lg md:text-xl text-gray-300 tracking-widest uppercase mb-2">
          Le goût authentique
        </p>
        <div className="flex items-center justify-center gap-3 text-orange-400 font-medium mb-8">
          <span>Bowls</span>
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
          <span>Tacos</span>
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
          <span>Crousty</span>
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
          <span>Kebabs</span>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-delay-1">
        <a href={restaurantInfo.phoneLink} data-testid="hero-call-button">
          <Button className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider px-8 py-4 rounded-md transition-all hover:scale-105 flex items-center gap-3 text-lg glow-orange" style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.4)' }}>
            <Phone className="w-5 h-5" />
            Commander
          </Button>
        </a>
        <Link to="/menu" data-testid="hero-menu-button">
          <Button variant="outline" className="bg-transparent border-2 border-white/30 hover:border-white text-white font-bold uppercase tracking-wider px-8 py-4 rounded-md transition-all hover:bg-white/5 flex items-center gap-3 text-lg">
            Voir le Menu
            <ArrowRight className="w-5 h-5" />
          </Button>
        </Link>
      </div>

      {/* Quick Info */}
      <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-gray-400 text-sm animate-fade-in-delay-2">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-orange-500" />
          <span>Chaumont-en-Vexin</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-orange-500" />
          <span>11h-14h / 18h-22h</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-orange-500" />
          <span>{restaurantInfo.phone}</span>
        </div>
      </div>
    </div>

    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
        <div className="w-1.5 h-3 bg-orange-500 rounded-full"></div>
      </div>
    </div>
  </section>
);

// Category Card Component
const CategoryCard = ({ title, description, image, badge, link, className = "" }) => (
  <Link to={link} className={`category-card block rounded-xl overflow-hidden ${className}`} data-testid={`category-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="relative h-full min-h-[200px] md:min-h-[300px]">
      <img 
        src={image} 
        alt={title}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10"></div>
      <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
        {badge && (
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-orange-500/20 text-orange-400 border border-orange-500/30 mb-3">
            {badge}
          </span>
        )}
        <h3 className="font-heading text-2xl md:text-3xl text-white uppercase mb-2">{title}</h3>
        <p className="text-gray-300 text-sm md:text-base">{description}</p>
      </div>
    </div>
  </Link>
);

// Categories Section
const CategoriesSection = () => (
  <section className="py-16 md:py-24 px-4 md:px-8 bg-[#050505]" data-testid="categories-section">
    <div className="max-w-7xl mx-auto">
      {/* Section Title */}
      <div className="text-center mb-12">
        <h2 className="font-heading text-3xl md:text-5xl text-white uppercase mb-4">
          Nos <span className="text-orange-500">Spécialités</span>
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Découvrez notre sélection de plats frais préparés avec passion
        </p>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CategoryCard
          title="Nos Bowls"
          description="Frais, sains et gourmands avec frites et fromage fondu"
          image="https://customer-assets.emergentagent.com/job_gourmet-chaumont/artifacts/cwjb33eq_IMG_8634.jpeg"
          badge="Populaire"
          link="/menu?tab=bowls"
          className="md:col-span-2 md:row-span-2"
        />
        <CategoryCard
          title="Le Crousty"
          description="Poulet frit maison croustillant"
          image="https://customer-assets.emergentagent.com/job_gourmet-chaumont/artifacts/s3euqovo_IMG_8632.jpeg"
          badge="Nouveau"
          link="/menu?tab=crousty"
        />
        <CategoryCard
          title="French Tacos"
          description="Sauce fromagère onctueuse"
          image="https://customer-assets.emergentagent.com/job_gourmet-chaumont/artifacts/hyc41tkc_IMG_8633.jpeg"
          link="/menu?tab=tacos"
        />
      </div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => (
  <section className="py-16 md:py-20 px-4 md:px-8 bg-[#0A0A0A]" data-testid="features-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Feature 1 */}
        <div className="text-center p-6 rounded-xl border border-white/5 bg-[#0f0f0f]">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/10 flex items-center justify-center">
            <Utensils className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="font-accent text-lg font-semibold text-white uppercase tracking-wider mb-2">
            Produits Frais
          </h3>
          <p className="text-gray-400 text-sm">
            Viandes et légumes sélectionnés chaque jour pour une qualité optimale
          </p>
        </div>

        {/* Feature 2 */}
        <div className="text-center p-6 rounded-xl border border-white/5 bg-[#0f0f0f]">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/10 flex items-center justify-center">
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="font-accent text-lg font-semibold text-white uppercase tracking-wider mb-2">
            Service Rapide
          </h3>
          <p className="text-gray-400 text-sm">
            Sur place ou à emporter, votre commande est prête en quelques minutes
          </p>
        </div>

        {/* Feature 3 */}
        <div className="text-center p-6 rounded-xl border border-white/5 bg-[#0f0f0f]">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/10 flex items-center justify-center">
            <MapPin className="w-8 h-8 text-orange-500" />
          </div>
          <h3 className="font-accent text-lg font-semibold text-white uppercase tracking-wider mb-2">
            Zone Étendue
          </h3>
          <p className="text-gray-400 text-sm">
            Gisors, Loconville, Liancourt-Saint-Pierre, Trie-Château et alentours
          </p>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-16 md:py-24 px-4 md:px-8 bg-[#050505]" data-testid="cta-section">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="font-heading text-3xl md:text-5xl text-white uppercase mb-6">
        Une envie de <span className="text-orange-500">Délices</span> ?
      </h2>
      <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
        Que vous soyez en Île-de-France, dans les Hauts-de-France, à Gisors ou dans les environs, 
        venez découvrir le goût authentique chez Délices de Chaumont.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <a href={restaurantInfo.phoneLink} data-testid="cta-call-button">
          <Button className="bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider px-10 py-5 rounded-md transition-all hover:scale-105 flex items-center gap-3 text-lg glow-orange">
            <Phone className="w-5 h-5" />
            {restaurantInfo.phone}
          </Button>
        </a>
        <Link to="/infos" data-testid="cta-info-button">
          <Button variant="outline" className="bg-transparent border-2 border-white/30 hover:border-white text-white font-bold uppercase tracking-wider px-10 py-5 rounded-md transition-all hover:bg-white/5">
            Nous trouver
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-8 px-4 md:px-8 bg-[#0A0A0A] border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-center md:text-left">
          <span className="font-heading text-lg text-white uppercase">
            Délices <span className="text-orange-500">de</span> Chaumont
          </span>
          <p className="text-gray-500 text-sm mt-1">
            {restaurantInfo.address}, {restaurantInfo.city}
          </p>
        </div>
        <div className="flex items-center gap-6 text-gray-400 text-sm">
          <a href={restaurantInfo.phoneLink} className="hover:text-orange-500 transition-colors">
            {restaurantInfo.phone}
          </a>
          <span>© 2024</span>
        </div>
      </div>
    </div>
  </footer>
);

export const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <HeroSection />
      <CategoriesSection />
      <FeaturesSection />
      <CTASection />
      <Footer />
    </div>
  );
};

export default HomePage;
